import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, canEditEquipment } from '../context/AuthContext';
import { ABOUT_TO_START } from '../load_test_components/steps';
import { CalibrationPageProvider } from '../context/CalibrationPageContext';
import CalibrationsPage from './CalibrationsPage';
import DataStreamPanel from '../components/DataStreamPanel';
import TrackerSerialField from '../components/TrackerSerialField';
import useLoggerSocket from '../hooks/useLoggerSocket';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Stack,
  MenuItem,
  Tooltip,
  Tabs,
  Tab,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  fetchLoadTestBoxes,
  createLoadTestBox,
  updateLoadTestBox,
  deleteLoadTestBox,
  attachLoggerToBox,
  fetchDataLoggers,
  createDataLogger,
  updateDataLogger,
  deleteDataLogger,
  generateLoggerIngestKey,
  fetchLoggerIngestKey,
  fetchRegions,
  fetchBusinessUnits,
} from '../services/api';

function getHealthColor(status) {
  switch (status) {
    // Manual lifecycle values (set in the edit dialogs)
    case 'Active': return '#00ff41';
    case 'Inactive': return '#ff1a1a';
    case 'Decommissioned': return '#555555';
    // Live values written by the data-logger SFTP poller (load_test.py)
    case 'writing': return '#00ff41';
    case 'online': return '#ffb300';
    case 'offline': return '#b9b9b9';
    default: return '#ff8c00';
  }
}

function getHealthLabel(status) {
  switch (status) {
    case 'writing': return 'Writing: receiving readings tagged with a job number';
    case 'online': return 'Online: receiving readings, but no job number';
    case 'offline': return 'Offline: no readings for over 3 minutes';
    case 'Active':
    case 'Inactive':
    case 'Decommissioned': return `${status} (set manually)`;
    default: return status || 'Unknown';
  }
}

function LoggerDot({ status, size }) {
  return (
    <Tooltip title={getHealthLabel(status)} arrow>
      <Box sx={{ width: size, height: size, borderRadius: '50%', backgroundColor: getHealthColor(status), flexShrink: 0 }} />
    </Tooltip>
  );
}

function StatusDot({ status, color, label }) {
  const c = color || getHealthColor(status);
  return (
    <Tooltip title={label || status || 'Unknown'} arrow>
      <Box sx={{
        width: 32, height: 32, borderRadius: '50%',
        background: `radial-gradient(circle, ${c} 45%, ${c}88 55%, ${c}22 70%, transparent 80%)`,
        flexShrink: 0,
      }} />
    </Tooltip>
  );
}

// A test box's status is derived from its logger, not stored: no logger means
// the box can't run a test (inactive); an attached-but-idle logger is amber.
const LOGGER_ACTIVE_STATUSES = ['Active', 'online', 'writing'];
function getBoxHealth(box) {
  if (!box.logger) return { color: '#9e9e9e', label: 'Inactive — no data logger assigned' };
  if (box.about_to_start) {
    return { color: ABOUT_TO_START.color, label: `${ABOUT_TO_START.label} — ${box.logger.name} is on site and online` };
  }
  if (LOGGER_ACTIVE_STATUSES.includes(box.logger.status)) {
    return { color: '#00ff41', label: `Active — ${box.logger.name} is ${box.logger.status === 'writing' ? 'writing' : 'online'}` };
  }
  return { color: '#ff8c00', label: 'Logger inactive' };
}

// The data-logger case photo with the unit name rendered on the blank top-left
// plate area, like the engraving on the real box. Position is a percentage of
// the image so it scales with the card (cqw = % of the wrapper's width), and
// the font shrinks for longer names so the text never runs into the logo.
function LoggerBoxImage({ name, showName = true, sx }) {
  const label = name || '';
  const fontCqw = Math.min(6.5, 25 / (0.62 * Math.max(label.length, 1))).toFixed(2);
  // The photo includes the grey case around the stainless faceplate; crop it
  // away in CSS by oversizing the image inside an overflow-hidden wrapper.
  // Crop margins (as % of the raw photo): 6.8% left/right, 6% top, 8% bottom.
  return (
    <Box sx={{ position: 'relative', containerType: 'inline-size', overflow: 'hidden', aspectRatio: '1050 / 771', lineHeight: 0, ...sx }}>
      <Box component="img" src="/data_loggers_base.jpg" alt={label}
           sx={{ position: 'absolute', width: '113.6%', left: '-6.8%', top: '-7%', display: 'block' }} />
      {showName && (
        <Typography sx={{ position: 'absolute', top: '5%', left: '3.5%', fontWeight: 800, color: '#1f1f1f', fontSize: `${fontCqw}cqw`, letterSpacing: '0.05em', lineHeight: 1, whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
      )}
    </Box>
  );
}

// State code from a Samsara city_state ('Mt. Lebanon, PA' -> 'PA'), or null.
function stateOf(location) {
  const cityState = location?.city_state;
  if (!cityState) return null;
  const parts = cityState.split(',').map(p => p.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : null;
}

// Calibration is due once a year; a box's date is its stalest sensor's latest
// calibration (computed server-side in load_test.py).
const CALIBRATION_MAX_AGE_DAYS = 365;
function calibrationInfo(box) {
  if (!box.last_calibrated) {
    return { text: box.uncalibrated_sensors ? 'No calibration on file' : 'No sensors assigned', overdue: false };
  }
  // Date-only ISO string; parse as local midnight so the shown day doesn't shift.
  const date = new Date(`${box.last_calibrated}T00:00:00`);
  const ageDays = (Date.now() - date.getTime()) / 86400000;
  return { text: date.toLocaleDateString(), overdue: ageDays > CALIBRATION_MAX_AGE_DAYS };
}

// Filter sentinels; real BU/state values never collide with these.
const ALL = '__all__';
const NONE = '__none__';

// URL ?tab= keys, so other pages (and the old Operations > Calibrations link)
// can deep-link a tab.
const TAB_KEYS = ['boxes', 'loggers', 'calibrations'];

const EMPTY_BOX_FORM = { name: '', biz_unit: '', region: 'General', tracker_serial: '', site_factor: '', status: 'Active', logger_id: '' };
// data_source: where the site reads this logger from — 'sftp' (its CSV on the
// SFTP server) or 'http' (readings the CR6 posts straight to the site). New
// loggers default to http; rows the SFTP sync created stay sftp until switched.
const EMPTY_LOGGER_FORM = {
  name: '', serial_number: '', biz_unit: '', region: 'General', status: 'Active', firmware_version: '',
  router_model: '', imei: '', sim: '', ip_address: '', load_test_box_id: '', data_source: 'http', tracker_serial: '',
};

const DATA_SOURCE_LABELS = { sftp: 'SFTP (CSV file)', http: 'HTTP (posts to site)' };

// "3 min ago" style age for a logger's last HTTP post; null when it never posted.
function lastPostLabel(iso) {
  if (!iso) return null;
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 90) return 'just now';
  if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)} h ago`;
  return `${Math.round(secs / 86400)} d ago`;
}

function DataLoggersPage() {
  const auth = useAuth() || {};
  const user = auth.user || null;
  const canEdit = canEditEquipment(user?.role);
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Calibrations is admin-only (its endpoints are admin-gated).
  const requestedTab = TAB_KEYS.indexOf(searchParams.get('tab'));
  const tab = requestedTab === -1 || (requestedTab === 2 && !isAdmin) ? 0 : requestedTab;
  const [buFilter, setBuFilter] = useState(ALL);
  const [stateFilter, setStateFilter] = useState(ALL);
  // Filter options differ per tab, so a pick from one tab doesn't carry over.
  const setTab = (v) => {
    setSearchParams({ tab: TAB_KEYS[v] }, { replace: true });
    setBuFilter(ALL);
    setStateFilter(ALL);
  };
  const [boxes, setBoxes] = useState([]);
  const [loggers, setLoggers] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  // Region options scoped to the BU picked in the test-box / logger dialogs
  const [boxRegions, setBoxRegions] = useState([]);
  const [loggerRegions, setLoggerRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [snack, setSnack] = useState('');

  // Test box add/edit dialog (editBox null = add)
  const [boxDialogOpen, setBoxDialogOpen] = useState(false);
  const [editBox, setEditBox] = useState(null);
  const [boxForm, setBoxForm] = useState(EMPTY_BOX_FORM);

  // Data logger dialogs (editLogger null = add)
  const [loggerReadOpen, setLoggerReadOpen] = useState(false);
  const [readLogger, setReadLogger] = useState(null);
  const [loggerDialogOpen, setLoggerDialogOpen] = useState(false);
  const [editLogger, setEditLogger] = useState(null);
  const [loggerForm, setLoggerForm] = useState(EMPTY_LOGGER_FORM);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // The open logger's ingest key, fetched on demand (admins only) and held
  // only while its dialog is open.
  const [revealedKey, setRevealedKey] = useState(null);
  const [keyVisible, setKeyVisible] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  useEffect(() => {
    if (!loggerDialogOpen) {
      setRevealedKey(null);
      setKeyVisible(false);
      setKeyCopied(false);
    }
  }, [loggerDialogOpen]);

  // Drag-and-drop: id of the box card currently hovered by a dragged logger
  const [dragOverBoxId, setDragOverBoxId] = useState(null);

  // Details / Data Stream tab of the box and logger dialogs (1 = Data Stream).
  const [boxTab, setBoxTab] = useState(0);
  const [loggerTab, setLoggerTab] = useState(0);

  // Live logger statuses from the WebSocket. Kept in a ref as well so a list
  // that loads after the first status message still gets them.
  const liveStatusRef = useRef(new Map());
  const withLiveStatus = (boxList, loggerList) => {
    const live = liveStatusRef.current;
    return [
      boxList.map(b => (b.logger && live.has(b.logger.id)
        ? { ...b, logger: { ...b.logger, status: live.get(b.logger.id).status } } : b)),
      loggerList.map(l => (live.has(l.id) ? { ...l, ...live.get(l.id) } : l)),
    ];
  };
  const applyStatuses = useCallback((updates) => {
    updates.forEach(u => liveStatusRef.current.set(u.id, { status: u.status, last_post_at: u.last_post_at }));
    const byId = new Map(updates.map(u => [u.id, u]));
    setLoggers(prev => prev.map(l => (byId.has(l.id)
      ? { ...l, status: byId.get(l.id).status, last_post_at: byId.get(l.id).last_post_at } : l)));
    setBoxes(prev => prev.map(b => (b.logger && byId.has(b.logger.id)
      ? { ...b, logger: { ...b.logger, status: byId.get(b.logger.id).status } } : b)));
  }, []);
  const { connection, stream, setStreamLoggerId } = useLoggerSocket(applyStatuses);

  const loadAll = async () => {
    try {
      const [boxData, loggerData] = await Promise.all([fetchLoadTestBoxes(), fetchDataLoggers()]);
      // The shared box-list endpoint (load_test.py blueprint) returns a bare array.
      const [liveBoxes, liveLoggers] = withLiveStatus(Array.isArray(boxData) ? boxData : [], loggerData.loggers || []);
      setBoxes(liveBoxes);
      setLoggers(liveLoggers);
    } catch (err) {
      console.error('Error loading data loggers/test boxes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    fetchBusinessUnits(user?.country).then(d => setBusinessUnits(d.businessUnits || [])).catch(() => {});
  }, []);

  // Without the live socket (server full or unreachable), keep statuses from
  // going stale by re-reading the lists every minute.
  useEffect(() => {
    if (connection === 'live') return undefined;
    const id = setInterval(loadAll, 60000);
    return () => clearInterval(id);
  }, [connection]);

  // The logger whose Data Stream tab is showing, if any. The box dialog shows
  // the stream straight away for users who can't edit (it's all they get).
  const boxStreamShown = boxDialogOpen && !!editBox?.logger && (boxTab === 1 || !canEdit);
  const streamLoggerId = boxStreamShown ? editBox.logger.id
    : loggerDialogOpen && editLogger && loggerTab === 1 ? editLogger.id
    : loggerReadOpen && readLogger && loggerTab === 1 ? readLogger.id
    : null;
  useEffect(() => { setStreamLoggerId(streamLoggerId); }, [streamLoggerId, setStreamLoggerId]);
  // The dialogs hold a snapshot of the logger; the stream wants its live status.
  const streamLogger = streamLoggerId == null ? null
    : loggers.find(l => l.id === streamLoggerId) || editBox?.logger || editLogger || readLogger;

  const unattachedLoggers = useMemo(() => loggers.filter(l => !l.load_test_box_id), [loggers]);

  // Loggers and test boxes each carry their own Samsara tracker.
  const loggerState = (l) => stateOf(l.tracker_location);

  // Filter options come from the rows on the active tab.
  const filterRows = tab === 1
    ? loggers.map(l => ({ bu: l.biz_unit, state: loggerState(l) }))
    : boxes.map(b => ({ bu: b.biz_unit, state: stateOf(b.tracker_location) }));
  const buOptions = [...new Set(filterRows.map(r => r.bu).filter(Boolean))].sort();
  const stateOptions = [...new Set(filterRows.map(r => r.state).filter(Boolean))].sort();
  const hasNoBu = filterRows.some(r => !r.bu);
  const hasNoState = filterRows.some(r => !r.state);

  const matchesFilters = (bu, state) =>
    (buFilter === ALL || (buFilter === NONE ? !bu : bu === buFilter)) &&
    (stateFilter === ALL || (stateFilter === NONE ? !state : state === stateFilter));

  const filteredBoxes = useMemo(() => {
    const term = search.trim().toLowerCase();
    return boxes.filter((b) =>
      matchesFilters(b.biz_unit, stateOf(b.tracker_location)) && (!term ||
        (b.name || '').toLowerCase().includes(term) ||
        (b.region || '').toLowerCase().includes(term) ||
        (b.tracker_serial || '').toLowerCase().includes(term) ||
        (b.logger?.name || '').toLowerCase().includes(term))
    );
  }, [boxes, search, buFilter, stateFilter]);

  const filteredLoggers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return loggers.filter((l) =>
      matchesFilters(l.biz_unit, loggerState(l)) && (!term ||
        (l.name || '').toLowerCase().includes(term) ||
        (l.serial_number || '').toLowerCase().includes(term) ||
        (l.box_name || '').toLowerCase().includes(term) ||
        (l.region || '').toLowerCase().includes(term) ||
        (l.ip_address || '').toLowerCase().includes(term) ||
        (l.tracker_serial || '').toLowerCase().includes(term))
    );
  }, [loggers, search, buFilter, stateFilter]);

  // Region groups for the loggers grid, same ordering as GPS Rovers:
  // General first, rest alphabetical, Unassigned last.
  const groupedLoggers = useMemo(() => {
    const groups = {};
    for (const l of filteredLoggers) {
      const key = l.region || '__unassigned__';
      if (!groups[key]) groups[key] = [];
      groups[key].push(l);
    }
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === '__unassigned__') return 1;
      if (b === '__unassigned__') return -1;
      if (a === 'General') return -1;
      if (b === 'General') return 1;
      return a.localeCompare(b);
    });
  }, [filteredLoggers]);

  // ---------- Test box handlers ----------

  const openAddBox = () => {
    setEditBox(null);
    setBoxTab(0);
    setBoxForm(EMPTY_BOX_FORM);
    setBoxRegions([]);
    setSaveError('');
    setBoxDialogOpen(true);
  };

  // Editors get the edit form (with a Data Stream tab when a logger is
  // attached); everyone else can open a box only to watch its logger's stream.
  const openBox = (box) => {
    if (canEdit) {
      openEditBox(box);
    } else if (box.logger) {
      setEditBox(box);
      setBoxTab(1);
      setBoxDialogOpen(true);
    }
  };

  const openEditBox = (box) => {
    setEditBox(box);
    setBoxTab(0);
    setBoxForm({
      name: box.name || '',
      biz_unit: box.biz_unit || '',
      region: box.region || 'General',
      tracker_serial: box.tracker_serial || '',
      site_factor: box.site_factor ?? '',
      status: box.status || 'Active',
      logger_id: box.logger?.id ?? '',
    });
    setBoxRegions([]);
    if (box.biz_unit) {
      fetchRegions(box.biz_unit, user?.country).then(d => setBoxRegions(d.regions || [])).catch(() => {});
    }
    setSaveError('');
    setBoxDialogOpen(true);
  };

  const handleBoxChange = (field) => (e) =>
    setBoxForm(prev => ({ ...prev, [field]: e.target.value }));

  const describeAttachResult = (res) => {
    const parts = [];
    if (res.moved_from) parts.push(`moved from ${res.moved_from.name}`);
    if (res.replaced_logger) parts.push(`${res.replaced_logger.name} is now unassigned`);
    return parts.length ? ` (${parts.join('; ')})` : '';
  };

  const saveBox = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        name: boxForm.name,
        biz_unit: boxForm.biz_unit,
        region: boxForm.region,
        tracker_serial: boxForm.tracker_serial,
        site_factor: boxForm.site_factor,
        status: boxForm.status,
        logger_id: boxForm.logger_id === '' ? null : boxForm.logger_id,
      };
      const res = editBox
        ? await updateLoadTestBox(editBox.id, payload)
        : await createLoadTestBox(payload);
      setBoxDialogOpen(false);
      setSnack(`${editBox ? 'Updated' : 'Added'} ${boxForm.name}${describeAttachResult(res)}`);
      await loadAll();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeBox = async () => {
    if (!editBox) return;
    if (!window.confirm(`Delete ${editBox.name}? Its data logger will become unassigned (not deleted).`)) return;
    setSaving(true);
    try {
      await deleteLoadTestBox(editBox.id);
      setBoxDialogOpen(false);
      setSnack(`Deleted ${editBox.name}`);
      await loadAll();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const detachLogger = async (box) => {
    try {
      await attachLoggerToBox(box.id, null);
      setSnack(`${box.logger?.name || 'Logger'} unassigned from ${box.name}`);
      await loadAll();
    } catch (err) {
      setSnack(err.message);
    }
  };

  // ---------- Drag-and-drop ----------

  const handleDragStart = (logger) => (e) => {
    e.dataTransfer.setData('text/plain', String(logger.id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (box) => async (e) => {
    e.preventDefault();
    setDragOverBoxId(null);
    const loggerId = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (!loggerId || (box.logger && box.logger.id === loggerId)) return;
    if (box.logger && !window.confirm(`${box.name} already has ${box.logger.name} assigned. Replace it?`)) return;
    try {
      const res = await attachLoggerToBox(box.id, loggerId);
      setSnack(`Logger assigned to ${box.name}${describeAttachResult(res)}`);
      await loadAll();
    } catch (err) {
      setSnack(err.message);
    }
  };

  // ---------- Data logger handlers ----------

  const openAddLogger = () => {
    setEditLogger(null);
    setLoggerTab(0);
    setLoggerForm(EMPTY_LOGGER_FORM);
    setLoggerRegions([]);
    setSaveError('');
    setLoggerDialogOpen(true);
  };

  const openLogger = (logger) => {
    setLoggerTab(0);
    if (!canEdit) {
      setReadLogger(logger);
      setLoggerReadOpen(true);
      return;
    }
    setEditLogger(logger);
    setLoggerRegions([]);
    if (logger.biz_unit) {
      fetchRegions(logger.biz_unit, user?.country).then(d => setLoggerRegions(d.regions || [])).catch(() => {});
    }
    setLoggerForm({
      name: logger.name || '',
      serial_number: logger.serial_number || '',
      biz_unit: logger.biz_unit || '',
      region: logger.region || 'General',
      status: logger.status || 'Active',
      firmware_version: logger.firmware_version || '',
      router_model: logger.router_model || '',
      imei: logger.imei || '',
      sim: logger.sim || '',
      ip_address: logger.ip_address || '',
      load_test_box_id: logger.load_test_box_id ?? '',
      data_source: logger.data_source || 'sftp',
      tracker_serial: logger.tracker_serial || '',
    });
    setSaveError('');
    setLoggerDialogOpen(true);
  };

  const issueIngestKey = async () => {
    if (!editLogger || (editLogger.has_ingest_key && editLogger.ingest_key_readable)) return;
    setSaving(true);
    setSaveError('');
    try {
      const { key } = await generateLoggerIngestKey(editLogger.id);
      setRevealedKey(key);
      setKeyVisible(true);
      setKeyCopied(false);
      // The open dialog holds its own copy of the logger; reflect the new key
      // there too so the field and caption update without reopening.
      setEditLogger(prev => (prev ? { ...prev, has_ingest_key: true, ingest_key_readable: true } : prev));
      await loadAll();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // The key is only fetched the first time it's shown or copied.
  const loadRevealedKey = async () => {
    if (revealedKey) return revealedKey;
    const { key } = await fetchLoggerIngestKey(editLogger.id);
    setRevealedKey(key);
    return key;
  };

  const toggleKeyVisible = async () => {
    if (keyVisible) { setKeyVisible(false); return; }
    try {
      await loadRevealedKey();
      setKeyVisible(true);
    } catch (err) {
      setSaveError(err.message);
    }
  };

  const copyIngestKey = async () => {
    try {
      const key = await loadRevealedKey();
      await navigator.clipboard.writeText(key);
      setKeyCopied(true);
    } catch (err) {
      setSaveError(err.message);
    }
  };

  const handleLoggerChange = (field) => (e) =>
    setLoggerForm(prev => ({ ...prev, [field]: e.target.value }));

  const saveLogger = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        ...loggerForm,
        load_test_box_id: loggerForm.load_test_box_id === '' ? null : loggerForm.load_test_box_id,
      };
      if (editLogger) {
        await updateDataLogger(editLogger.id, payload);
      } else {
        await createDataLogger(payload);
      }
      setLoggerDialogOpen(false);
      setSnack(`${editLogger ? 'Updated' : 'Added'} ${loggerForm.name}`);
      await loadAll();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeLogger = async () => {
    if (!editLogger) return;
    if (!window.confirm(`Delete ${editLogger.name}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await deleteDataLogger(editLogger.id);
      setLoggerDialogOpen(false);
      setSnack(`Deleted ${editLogger.name}`);
      await loadAll();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Loggers offered in the test-box dialog: unassigned ones, plus whichever is
  // already assigned to the box being edited (so it stays selectable).
  const boxDialogLoggers = useMemo(() => {
    const current = editBox?.logger ? loggers.filter(l => l.id === editBox.logger.id) : [];
    return [...current, ...unattachedLoggers];
  }, [editBox, loggers, unattachedLoggers]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        // Three tabs overflow a phone; without this the last one is unreachable.
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 0.5, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { minWidth: 'auto' } }}
      >
        <Tab label="Test Boxes" />
        <Tab label="Data Loggers" />
        {isAdmin && <Tab label="Calibrations" />}
      </Tabs>

      {/* ---------- Calibrations tab (moved here from Operations) ---------- */}
      {tab === 2 && (
        // Fixed height: the calibration panes size themselves to 100% of it.
        <Box sx={{ height: { xs: 'auto', md: 'calc(100vh - 64px - 56px - 48px - 56px)' }, minHeight: { xs: 0, md: 520 }, mt: 1 }}>
          <CalibrationPageProvider>
            <CalibrationsPage />
          </CalibrationPageProvider>
        </Box>
      )}

      {/* Controls Row — mt gives a small gap under the tab underline */}
      {tab !== 2 && (
      <Grid container spacing={2} sx={{ mt: '2px', mb: 1.5 }}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, height: '100%' }}>
            <FormControl size="small" sx={{ minWidth: 170, bgcolor: 'background.paper' }}>
              <InputLabel>Business Unit</InputLabel>
              <Select value={buFilter} label="Business Unit" onChange={(e) => setBuFilter(e.target.value)}>
                <MenuItem value={ALL}>All business units</MenuItem>
                {buOptions.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                {hasNoBu && <MenuItem value={NONE}><em>No business unit</em></MenuItem>}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper' }}>
              <InputLabel>Location</InputLabel>
              <Select value={stateFilter} label="Location" onChange={(e) => setStateFilter(e.target.value)}>
                <MenuItem value={ALL}>All locations</MenuItem>
                {stateOptions.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
                {hasNoState && <MenuItem value={NONE}><em>No location</em></MenuItem>}
              </Select>
            </FormControl>
            {tab === 0 && (
              <Button
                variant="outlined"
                startIcon={<MapOutlinedIcon />}
                onClick={() => navigate('/equipment/map?layers=testBoxes')}
                sx={{ bgcolor: 'background.paper', textTransform: 'none', fontWeight: 600 }}
              >
                View on Map
              </Button>
            )}
            <TextField
              size="small"
              variant="outlined"
              placeholder={tab === 0 ? 'Search test boxes...' : 'Search data loggers...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: 260,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                  '& fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                  '&:hover fieldset': { borderColor: 'primary.dark' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
            {canEdit && (
              <Tooltip title={tab === 0 ? 'Add Test Box' : 'Add Data Logger'} arrow>
                <IconButton
                  onClick={tab === 0 ? openAddBox : openAddLogger}
                  sx={{ backgroundColor: 'secondary.main', color: 'white', '&:hover': { backgroundColor: 'secondary.dark' }, width: 36, height: 36 }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Grid>
      </Grid>
      )}

      {/* ---------- Test Boxes tab ---------- */}
      {tab === 0 && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            {filteredBoxes.length === 0 && (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {boxes.length === 0 ? 'No test boxes yet. Use + to add one.' : 'No test boxes match your search or filters.'}
                </Typography>
              </Paper>
            )}
            <Grid container spacing={2}>
              {filteredBoxes.map((box) => {
                const cal = calibrationInfo(box);
                const loc = box.tracker_location;
                return (
                <Grid item xs={12} sm={6} lg={4} key={box.id}>
                  <Card
                    onClick={() => openBox(box)}
                    onDragOver={canEdit ? (e) => { e.preventDefault(); setDragOverBoxId(box.id); } : undefined}
                    onDragLeave={canEdit ? () => setDragOverBoxId(null) : undefined}
                    onDrop={canEdit ? handleDrop(box) : undefined}
                    sx={{
                      height: '100%',
                      border: 2,
                      // Red highlight while dragging over an occupied box — the
                      // drop would replace its current logger.
                      borderColor: dragOverBoxId === box.id ? (box.logger ? 'error.main' : 'primary.main') : cal.overdue ? 'error.main' : 'divider',
                      boxShadow: dragOverBoxId === box.id ? 6 : 1,
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: 4, cursor: canEdit || box.logger ? 'pointer' : 'default' },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>{box.name || `Test Box ${box.id}`}</Typography>
                        <StatusDot {...getBoxHealth(box)} />
                      </Box>
                      <Stack spacing={0.25} sx={{ mb: 1.5 }}>
                        <Stack direction="row" spacing={0.5}>
                          <Typography variant="body2">Business Unit:</Typography>
                          <Typography variant="body2" color="text.secondary">{box.biz_unit || '-'}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5}>
                          <Typography variant="body2">Region:</Typography>
                          <Typography variant="body2" color="text.secondary">{box.region || '-'}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2">Location:</Typography>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                            {loc?.city_state || (box.tracker_serial ? 'No tracker fix' : 'No tracker')}
                          </Typography>
                          {loc?.lat != null && loc?.lon != null && (
                            <Tooltip title="Show on equipment map" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); navigate(`/equipment/map?layers=testBoxes&focus=box-${box.id}`); }}
                                sx={{ p: 0.25 }}
                              >
                                <MapOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                        <Stack direction="row" spacing={0.5}>
                          <Typography variant="body2">Last Calibrated:</Typography>
                          <Tooltip
                            arrow
                            title={cal.overdue ? 'Over a year since the oldest sensor calibration — recalibration due' : "Oldest of the assigned sensors' latest calibrations"}
                          >
                            <Typography
                              variant="body2"
                              color={cal.overdue ? 'error.main' : 'text.secondary'}
                              fontWeight={cal.overdue ? 700 : 400}
                            >
                              {cal.text}
                            </Typography>
                          </Tooltip>
                        </Stack>
                      </Stack>
                      {box.logger ? (
                        // Green when the logger was auto-assigned by tracker proximity, blue when assigned by hand.
                        <Tooltip
                          arrow
                          title={box.logger.assignment_source === 'proximity' ? 'Auto-assigned based on proximity' : 'Manual assignment'}
                        >
                          <Chip
                            label={box.logger.name}
                            color={box.logger.assignment_source === 'proximity' ? 'success' : 'primary'}
                            size="small"
                            onDelete={canEdit ? (e) => { e.stopPropagation(); detachLogger(box); } : undefined}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ fontWeight: 600 }}
                          />
                        </Tooltip>
                      ) : (
                        <Box sx={{
                          border: '2px dashed',
                          borderColor: dragOverBoxId === box.id ? 'primary.main' : 'divider',
                          borderRadius: 1, px: 1.5, py: 1, textAlign: 'center',
                        }}>
                          <Typography variant="body2" color="text.secondary">
                            {canEdit ? 'Drop a logger here or edit to assign' : 'No data logger assigned'}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Unassigned loggers rail — drag sources */}
          {canEdit && (
            <Paper sx={{ width: 260, flexShrink: 0, p: 2, display: { xs: 'none', md: 'block' } }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                Unassigned Loggers
              </Typography>
              {unattachedLoggers.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Every logger is assigned to a test box. Add one on the Data Loggers tab.
                </Typography>
              )}
              <Stack spacing={1}>
                {unattachedLoggers.map((logger) => (
                  <Paper
                    key={logger.id}
                    draggable
                    onDragStart={handleDragStart(logger)}
                    sx={{
                      px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1,
                      border: 1, borderColor: 'divider', cursor: 'grab',
                      '&:hover': { boxShadow: 3 }, '&:active': { cursor: 'grabbing' },
                    }}
                  >
                    <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>{logger.name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {logger.serial_number || logger.ip_address || logger.status || ''}
                      </Typography>
                    </Box>
                    <LoggerDot status={logger.status} size={10} />
                  </Paper>
                ))}
              </Stack>
            </Paper>
          )}
        </Box>
      )}

      {/* ---------- Data Loggers tab — rover-style grid grouped by region ---------- */}
      {tab === 1 && (
        <>
          {filteredLoggers.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {loggers.length === 0 ? 'No data loggers yet. Use + to add one.' : 'No data loggers match your search or filters.'}
              </Typography>
            </Paper>
          )}

          {groupedLoggers.map(([regionKey, regionLoggers]) => (
            <Box key={regionKey} sx={{ mb: 4 }}>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="h6" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  {regionKey === '__unassigned__' ? 'Unassigned' : regionKey}
                </Typography>
              </Box>
              <Box sx={{ mb: 2, height: '2px', backgroundColor: 'divider' }} />

              <Grid container spacing={1.5}>
                {regionLoggers.map((logger) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={logger.id}>
                    <Card
                      onClick={() => openLogger(logger)}
                      sx={{
                        border: 1,
                        borderColor: logger.load_test_box_id ? 'primary.main' : 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                      }}
                    >
                      <CardContent sx={{ p: '10px !important' }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          {/* Info */}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                              <LoggerDot status={logger.status} size={9} />
                              <Typography variant="body2" fontWeight={700} noWrap>{logger.name}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, whiteSpace: { xs: 'normal', sm: 'nowrap' }, overflowWrap: 'anywhere' }}>
                              {logger.box_name || 'No test box'}
                            </Typography>
                            {logger.tracker_serial && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 0.25 }}>
                                {logger.tracker_location?.city_state || 'No tracker fix'}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {logger.status || '—'}{logger.data_source === 'http' ? ` · HTTP${lastPostLabel(logger.last_post_at) ? `, ${lastPostLabel(logger.last_post_at)}` : ''}` : ''}
                            </Typography>
                          </Box>
                          {/* Faceplate thumbnail */}
                          <LoggerBoxImage name={logger.name} showName={false} sx={{ width: { xs: 52, sm: 84 }, flexShrink: 0, borderRadius: 1 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </>
      )}

      {/* ---------- Test box add/edit dialog ---------- */}
      <Dialog open={boxDialogOpen} disableScrollLock onClose={() => setBoxDialogOpen(false)} fullWidth maxWidth={boxStreamShown ? 'md' : 'sm'}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
          <Typography variant='h6' fontWeight={700}>{editBox ? editBox.name : 'Add Test Box'}</Typography>
          <IconButton size='small' onClick={() => setBoxDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        {canEdit && editBox?.logger && (
          <Tabs value={boxTab} onChange={(e, v) => setBoxTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label='Details' />
            <Tab label='Data Stream' />
          </Tabs>
        )}
        <DialogContent sx={{ pb: 1 }}>
          {boxStreamShown ? (
            <Box sx={{ pt: 2 }}>
              <DataStreamPanel logger={streamLogger} stream={stream} connection={connection} />
            </Box>
          ) : (
          <>
          <Grid container columnSpacing={1} rowSpacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField size='small' label='Name' value={boxForm.name} onChange={handleBoxChange('name')} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size='small' fullWidth required>
                <InputLabel shrink>Business Unit</InputLabel>
                <Select
                  value={boxForm.biz_unit}
                  label='Business Unit'
                  displayEmpty
                  renderValue={(v) => v || <em>Select business unit</em>}
                  onChange={(e) => {
                    const bu = e.target.value;
                    // Region resets to the General default — the old value may not belong to the new BU.
                    setBoxForm(prev => ({ ...prev, biz_unit: bu, region: 'General' }));
                    fetchRegions(bu, user?.country).then(d => setBoxRegions(d.regions || [])).catch(() => {});
                  }}
                >
                  {businessUnits.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size='small' fullWidth>
                <InputLabel shrink>Region</InputLabel>
                <Select
                  value={boxForm.region}
                  label='Region'
                  displayEmpty
                  disabled={!boxForm.biz_unit}
                  renderValue={(v) => v || <em>{boxForm.biz_unit ? 'Select region' : 'Select business unit first'}</em>}
                  onChange={handleBoxChange('region')}
                >
                  {[...boxRegions, ...(!boxRegions.includes('General') ? ['General'] : [])].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size='small' fullWidth>
                <InputLabel>Data Logger</InputLabel>
                <Select value={boxForm.logger_id} label='Data Logger' onChange={handleBoxChange('logger_id')}>
                  <MenuItem value=''><em>None</em></MenuItem>
                  {boxDialogLoggers.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TrackerSerialField size='small' label='Samsara Tracker Serial' value={boxForm.tracker_serial} onChange={handleBoxChange('tracker_serial')} fullWidth
                kind='box' unitId={editBox?.id} originalValue={editBox?.tracker_serial} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size='small' label='Site Factor' type='number' value={boxForm.site_factor} onChange={handleBoxChange('site_factor')} fullWidth />
            </Grid>
            {editBox && (
              <Grid item xs={12} sm={6}>
                <FormControl size='small' fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={boxForm.status} label='Status' onChange={handleBoxChange('status')}>
                    <MenuItem value='Active'>Active</MenuItem>
                    <MenuItem value='Inactive'>Inactive</MenuItem>
                    <MenuItem value='Decommissioned'>Decommissioned</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
          {saveError && (
            <Typography variant='body2' color='error' sx={{ mt: 2 }}>{saveError}</Typography>
          )}
          </>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          {boxStreamShown ? (
            <Button variant='text' onClick={() => setBoxDialogOpen(false)}>Close</Button>
          ) : (
            <>
              {editBox && (
                <Button color='error' startIcon={<DeleteOutlineIcon />} onClick={removeBox} disabled={saving} sx={{ mr: 'auto' }}>
                  Delete
                </Button>
              )}
              <Button variant='text' onClick={() => setBoxDialogOpen(false)}>Cancel</Button>
              <Button variant='contained' onClick={saveBox} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ---------- Data logger read-only dialog ---------- */}
      <Dialog open={loggerReadOpen} disableScrollLock onClose={() => setLoggerReadOpen(false)} fullWidth maxWidth={loggerTab === 1 ? 'md' : 'sm'}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant='h6' fontWeight={700}>{readLogger?.name}</Typography>
          <IconButton onClick={() => setLoggerReadOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <Tabs value={loggerTab} onChange={(e, v) => setLoggerTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label='Details' />
          <Tab label='Data Stream' />
        </Tabs>
        <DialogContent sx={{ pb: 3 }}>
          {loggerTab === 1 && readLogger ? (
            <Box sx={{ pt: 2 }}>
              <DataStreamPanel logger={streamLogger || readLogger} stream={stream} connection={connection} />
            </Box>
          ) : readLogger && (
            <Box>
              <LoggerBoxImage name={readLogger.name} sx={{ width: '34%', mx: 'auto', mb: 2, borderRadius: 1 }} />
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item sm={6}>
                  <Stack direction='row' spacing={0.5}>
                    <Typography variant='body2'>Status:</Typography>
                    <Typography variant='body2' color='text.secondary'>{readLogger.status || '-'}</Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Typography variant='body1' sx={{ my: 1 }}>Details</Typography>

              <Grid container columnSpacing={1} rowSpacing={0.5} alignItems='center' sx={{ mb: 2 }}>
                {[
                  ['Logger Serial Number', readLogger.serial_number],
                  ['Firmware Version', readLogger.firmware_version],
                  ['Router Model', readLogger.router_model],
                  ['IMEI', readLogger.imei],
                  ['SIM', readLogger.sim],
                  ['IP Address', readLogger.ip_address],
                  ['Samsara Tracker Serial', readLogger.tracker_serial],
                  ['Location', readLogger.tracker_location?.city_state || (readLogger.tracker_serial ? 'No tracker fix' : null)],
                  ['Data Source', DATA_SOURCE_LABELS[readLogger.data_source] || readLogger.data_source],
                  ...(readLogger.data_source === 'http'
                    ? [['Last HTTP Post', lastPostLabel(readLogger.last_post_at) || 'Never']]
                    : []),
                ].map(([label, value]) => (
                  <Grid item sm={6} key={label}>
                    <Stack direction='row' spacing={0.5}>
                      <Typography variant='body2'>{label}:</Typography>
                      <Typography variant='body2' color='text.secondary'>{value || '-'}</Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={1} alignItems='center'>
                <Grid item sm={6}>
                  <Stack direction='row' spacing={0.5}>
                    <Typography variant='body2'>Assigned Test Box:</Typography>
                    <Typography variant='body2' color='text.secondary'>{readLogger.box_name || '-'}</Typography>
                  </Stack>
                </Grid>
                <Grid item sm={6}>
                  <Stack direction='row' spacing={0.5}>
                    <Typography variant='body2'>Business Unit:</Typography>
                    <Typography variant='body2' color='text.secondary'>{readLogger.biz_unit || '-'}</Typography>
                  </Stack>
                </Grid>
                <Grid item sm={6}>
                  <Stack direction='row' spacing={0.5}>
                    <Typography variant='body2'>Assigned Region:</Typography>
                    <Typography variant='body2' color='text.secondary'>{readLogger.region || '-'}</Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Data logger add/edit dialog ---------- */}
      <Dialog open={loggerDialogOpen} disableScrollLock onClose={() => setLoggerDialogOpen(false)} fullWidth maxWidth={editLogger && loggerTab === 1 ? 'md' : 'sm'}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
          <Typography variant='h6' fontWeight={700}>{editLogger ? editLogger.name : 'Add Data Logger'}</Typography>
          <IconButton size='small' onClick={() => setLoggerDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        {editLogger && (
          <Tabs value={loggerTab} onChange={(e, v) => setLoggerTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label='Details' />
            <Tab label='Data Stream' />
          </Tabs>
        )}
        <DialogContent sx={{ pb: 1 }}>
          {editLogger && loggerTab === 1 ? (
            <Box sx={{ pt: 2 }}>
              <DataStreamPanel logger={streamLogger || editLogger} stream={stream} connection={connection} />
            </Box>
          ) : (
          <>
          <LoggerBoxImage name={loggerForm.name} sx={{ width: '34%', mx: 'auto', mb: 2, mt: 0.5, borderRadius: 1 }} />
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item sm={6}>
              <TextField size='small' label='Name' value={loggerForm.name} onChange={handleLoggerChange('name')} fullWidth required />
            </Grid>
            <Grid item sm={6}>
              <FormControl size='small' fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={loggerForm.status} label='Status' onChange={handleLoggerChange('status')}>
                  {/* Live statuses are written by the SFTP poller; keep the current
                      one selectable so the field doesn't blank out. */}
                  {loggerForm.status && !['Active', 'Inactive', 'Decommissioned'].includes(loggerForm.status) && (
                    <MenuItem value={loggerForm.status}>{loggerForm.status} (live)</MenuItem>
                  )}
                  <MenuItem value='Active'>Active</MenuItem>
                  <MenuItem value='Inactive'>Inactive</MenuItem>
                  <MenuItem value='Decommissioned'>Decommissioned</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant='body1' sx={{ my: 1 }}>Details</Typography>

          <Grid container columnSpacing={1} rowSpacing={2} alignItems='center' sx={{ mb: 2 }}>
            <Grid item sm={6}>
              <TextField size='small' label='Logger Serial #' value={loggerForm.serial_number} onChange={handleLoggerChange('serial_number')} fullWidth />
            </Grid>
            <Grid item sm={6}>
              <TextField size='small' label='Firmware Version' value={loggerForm.firmware_version} onChange={handleLoggerChange('firmware_version')} fullWidth />
            </Grid>
            <Grid item sm={6}>
              <TextField size='small' label='Router Model' value={loggerForm.router_model} onChange={handleLoggerChange('router_model')} fullWidth />
            </Grid>
            <Grid item sm={6}>
              <TextField size='small' label='IMEI' value={loggerForm.imei} onChange={handleLoggerChange('imei')} fullWidth />
            </Grid>
            <Grid item sm={6}>
              <TextField size='small' label='SIM' value={loggerForm.sim} onChange={handleLoggerChange('sim')} fullWidth />
            </Grid>
            <Grid item sm={6}>
              <TextField size='small' label='IP Address' value={loggerForm.ip_address} onChange={handleLoggerChange('ip_address')} fullWidth />
            </Grid>
            <Grid item sm={6}>
              <TrackerSerialField size='small' label='Samsara Tracker Serial' value={loggerForm.tracker_serial} onChange={handleLoggerChange('tracker_serial')} fullWidth
                kind='logger' unitId={editLogger?.id} originalValue={editLogger?.tracker_serial} />
            </Grid>
          </Grid>

          <Typography variant='body1' sx={{ my: 1 }}>Data Transfer</Typography>

          {/* flex-start so the key field's top lines up with the Data Source
              select; its caption hangs underneath instead of centring the pair. */}
          <Grid container columnSpacing={1} rowSpacing={2} alignItems='flex-start' sx={{ mb: 2 }}>
            <Grid item sm={6}>
              <FormControl size='small' fullWidth>
                <InputLabel>Data Source</InputLabel>
                <Select value={loggerForm.data_source} label='Data Source' onChange={handleLoggerChange('data_source')}>
                  <MenuItem value='http'>{DATA_SOURCE_LABELS.http}</MenuItem>
                  <MenuItem value='sftp'>{DATA_SOURCE_LABELS.sftp}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item sm={6}>
              {/* The key only matters to a logger that posts over HTTP; keep it
                  out of sight for SFTP loggers so it can't be mistaken for an
                  SFTP credential. */}
              {loggerForm.data_source !== 'http' ? null : !editLogger ? (
                <Typography variant='caption' color='text.secondary'>
                  Save the logger first, then generate its ingest key.
                </Typography>
              ) : !isAdmin ? (
                // Technicians can see that a key exists, never the key itself.
                <Typography variant='caption' color='text.secondary'>
                  {editLogger.has_ingest_key
                    ? `Key issued · last post ${lastPostLabel(editLogger.last_post_at) || 'never'}`
                    : 'No key issued'}
                </Typography>
              ) : !(editLogger.has_ingest_key && editLogger.ingest_key_readable) ? (
                <Stack spacing={0.5} alignItems='flex-start'>
                  <Button size='small' variant='outlined' startIcon={<KeyIcon />} onClick={issueIngestKey} disabled={saving} sx={{ whiteSpace: 'nowrap' }}>
                    Generate ingest key
                  </Button>
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11, lineHeight: 1.3 }}>
                    {editLogger.has_ingest_key
                      ? 'Current key can\'t be shown; generate a new one and update the program'
                      : 'No key issued'}
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={0.5}>
                  <TextField
                    size='small'
                    label='Ingest key'
                    fullWidth
                    value={keyVisible && revealedKey ? revealedKey : '••••••••••••••••••••••••'}
                    InputProps={{
                      readOnly: true,
                      // height matches the small Select beside it; the smaller
                      // monospace font would otherwise shrink the box to 34px
                      sx: { fontFamily: 'monospace', fontSize: 12, height: 40 },
                      endAdornment: (
                        <InputAdornment position='end'>
                          {/* span wrappers: MUI Tooltip can't listen on a disabled button */}
                          <Tooltip title={keyVisible ? 'Hide' : 'Show'} arrow>
                            <span>
                              <IconButton size='small' onClick={toggleKeyVisible} disabled={saving}>
                                {keyVisible ? <VisibilityOffIcon fontSize='small' /> : <VisibilityIcon fontSize='small' />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={keyCopied ? 'Copied' : 'Copy'} arrow>
                            <span>
                              <IconButton size='small' onClick={copyIngestKey} disabled={saving}>
                                <ContentCopyIcon fontSize='small' />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    onFocus={(e) => keyVisible && e.target.select()}
                  />
                  {/* One key per logger for its lifetime: no rotate button, so a
                      box's program never silently stops matching. */}
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11, lineHeight: 1.3 }}>
                    Key issued · last post {lastPostLabel(editLogger.last_post_at) || 'never'}
                  </Typography>
                </Stack>
              )}
            </Grid>
          </Grid>

          <Grid container spacing={1} alignItems='center'>
            <Grid item sm={6}>
              <FormControl size='small' fullWidth sx={{ mt: 1 }}>
                <InputLabel>Assign Test Box</InputLabel>
                <Select value={loggerForm.load_test_box_id} label='Assign Test Box' onChange={handleLoggerChange('load_test_box_id')}>
                  <MenuItem value=''><em>None</em></MenuItem>
                  {boxes.map((b) => <MenuItem key={b.id} value={b.id}>{b.name || `Test Box ${b.id}`}</MenuItem>)}
                </Select>
                {editLogger?.load_test_box_id != null && loggerForm.load_test_box_id === editLogger.load_test_box_id && (
                  <FormHelperText sx={{ color: editLogger.assignment_source === 'proximity' ? 'success.main' : undefined }}>
                    {editLogger.assignment_source === 'proximity' ? 'Auto-assigned based on proximity' : 'Manual assignment'}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item sm={6}>
              <FormControl size='small' fullWidth sx={{ mt: 1 }} required>
                <InputLabel shrink>Business Unit</InputLabel>
                <Select
                  value={loggerForm.biz_unit}
                  label='Business Unit'
                  displayEmpty
                  renderValue={(v) => v || <em>Select business unit</em>}
                  onChange={(e) => {
                    const bu = e.target.value;
                    // Region resets to the General default — the old value may not belong to the new BU.
                    setLoggerForm(prev => ({ ...prev, biz_unit: bu, region: 'General' }));
                    fetchRegions(bu, user?.country).then(d => setLoggerRegions(d.regions || [])).catch(() => {});
                  }}
                >
                  {businessUnits.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item sm={6}>
              <FormControl size='small' fullWidth sx={{ mt: 1 }}>
                <InputLabel shrink>Region</InputLabel>
                <Select
                  value={loggerForm.region}
                  label='Region'
                  displayEmpty
                  disabled={!loggerForm.biz_unit}
                  renderValue={(v) => v || <em>{loggerForm.biz_unit ? 'Select region' : 'Select business unit first'}</em>}
                  onChange={handleLoggerChange('region')}
                >
                  {[...loggerRegions, ...(!loggerRegions.includes('General') ? ['General'] : [])].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {saveError && (
            <Typography variant='body2' color='error' sx={{ mt: 2 }}>{saveError}</Typography>
          )}
          </>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          {editLogger && loggerTab === 1 ? (
            <Button variant='text' onClick={() => setLoggerDialogOpen(false)}>Close</Button>
          ) : (
            <>
              {editLogger && (
                <Button color='error' startIcon={<DeleteOutlineIcon />} onClick={removeLogger} disabled={saving} sx={{ mr: 'auto' }}>
                  Delete
                </Button>
              )}
              <Button variant='text' onClick={() => setLoggerDialogOpen(false)}>Cancel</Button>
              <Button variant='contained' onClick={saveLogger} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default DataLoggersPage;
